import type { RequestInfo } from '@ember-data/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';

function baseUrl(): string {
  const options: QueryUrlOptions = {
    identifier: { type: 'payment-plan-explorer' },
    op: 'query',
    resourcePath: 'api/v2/developer/payment-plan-explorer',
  };
  return buildBaseURL(options);
}

// Employee-accessible (non-developer) payment-plan-explorer base path. Only the
// `generate` action is exposed here; it is pure calculation over caller inputs.
function employeeBaseUrl(): string {
  const options: QueryUrlOptions = {
    identifier: { type: 'payment-plan-explorer' },
    op: 'query',
    resourcePath: 'api/v2/payment-plan-explorer',
  };
  return buildBaseURL(options);
}

export interface PaymentEventPayload {
  title: string;
  amount: number; // cents
  month: number;
  month_end: number;
  source: string; // 'employee' | 'employer' | 'forgiveness'
}

export interface GeneratePaymentPlanPayload {
  interest_rate_annual: number;
  payment_plan: string;
  current_balance: number; // cents
  paid_months: number;
  original_disbursement_amount?: number | null; // cents
  agi: number; // cents
  agi_growth_rate: number;
  number_of_children: number;
  number_of_adults: number;
  first_loan_time: string;
  last_loan_time: string;
  loan_type: string;
  tlf_amount: number; // cents
  pslf: boolean;
  residence_state?: string | null;
  payment_events: PaymentEventPayload[];
  // This loan's Direct/FFEL balance share, used to split household-level
  // income-driven payments and TLF across loan groups. 1.0 unless multi-loan.
  multiplier?: number;
}

export interface AvailablePlansPayload {
  first_loan_time: string;
  last_loan_time: string;
  loan_type: string;
}

export interface PaymentPlanRow {
  month: number;
  payment: number;
  cumulative_payment: number;
  employer_payment: number;
  subsidy_amount: number;
  forgiveness_amount: number;
  balance: number;
}

export interface GeneratePaymentPlanResponse {
  rows: PaymentPlanRow[];
  title: string;
  employee_paid: number;
  employer_paid: number;
  forgiveness_paid: number;
  subsidy_paid: number;
  final_balance: number;
  monthly_payment?: number;
  monthly_payment_low?: number;
  monthly_payment_high?: number;
  warning?: string;
  error?: string;
  is_error?: boolean;
}

export interface AvailablePlansResponse {
  plans: string[];
  warning?: string;
}

export interface SortPlansPayload {
  interest_rate_annual: number;
  current_balance: number; // cents
  current_paid_months: number;
  current_plan: string | null;
  original_disbursement_amount?: number | null; // cents
  agi: number; // cents
  agi_growth_rate: number;
  number_of_children: number;
  number_of_adults: number;
  first_loan_time: string;
  last_loan_time: string;
  loan_type: string;
  tlf_amounts: number[]; // cents, e.g. [0] or [0, 500000] or [0, 1750000]
  pslf: boolean;
  residence_state?: string | null;
  payment_events: PaymentEventPayload[];
  sort_by: string;
  // This loan group's share of the combined Direct + FFEL balance, so the
  // income-driven payment and TLF are split across groups in a multi-loan
  // profile. Defaults to 1 (single group) when omitted.
  multiplier?: number;
}

export interface SortPlanSummary {
  plan: string;
  title: string;
  tlf_amount: number;
  first_month_payment: number;
  max_monthly_payment: number;
  total_payment: number;
  employee_paid: number;
  employer_paid: number;
  forgiveness_paid: number;
  subsidy_paid: number;
  num_payments: number;
  paid_months_used: number;
  paid_months_preserved: boolean;
  warning?: string | null;
}

export interface SortPlansResponse {
  plans: SortPlanSummary[];
  sort_by: string;
  available_plans_warning?: string | null;
  error?: string;
  is_error?: boolean;
}

export function generatePaymentPlanRequest(payload: GeneratePaymentPlanPayload): RequestInfo {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');
  return {
    url: `${baseUrl()}/generate`,
    method: 'POST',
    body: JSON.stringify(payload),
    headers,
    cacheOptions: { [SkipCache]: true },
  };
}

// Employee-facing variant of generate, hitting the non-developer endpoint.
export function generatePaymentPlanForUserRequest(
  payload: GeneratePaymentPlanPayload
): RequestInfo {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');
  return {
    url: `${employeeBaseUrl()}/generate`,
    method: 'POST',
    body: JSON.stringify(payload),
    headers,
    cacheOptions: { [SkipCache]: true },
  };
}

export function getAvailablePlansRequest(payload: AvailablePlansPayload): RequestInfo {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');
  return {
    url: `${baseUrl()}/available-plans`,
    method: 'POST',
    body: JSON.stringify(payload),
    headers,
    cacheOptions: { [SkipCache]: true },
  };
}

export function getSortedPlansRequest(payload: SortPlansPayload): RequestInfo {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');
  return {
    url: `${baseUrl()}/sort-plans`,
    method: 'POST',
    body: JSON.stringify(payload),
    headers,
    cacheOptions: { [SkipCache]: true },
  };
}
